import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';

export default function Activities({ api, user }) {
  const [activities, setActivities] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // View controls
  const [viewMode, setViewMode] = useState('hoje'); // hoje, proximos, semana, todas
  const [filterType, setFilterType] = useState('all'); // all, call, email, meeting, task
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, completed, overdue
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const [newActivity, setNewActivity] = useState({
    activity_type: 'call',
    subject: '',
    description: '',
    due_date: '',
    duration_minutes: 30,
    deal_id: ''
  });

  useEffect(() => {
    initializeData();
  }, []);

  const initializeData = async () => {
    try {
      await Promise.all([
        fetchAllActivities(),
        fetchDeals()
      ]);
    } catch (error) {
      console.error('Error initializing activities data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllActivities = async () => {
    try {
      // Use different endpoint based on user role
      const endpoint = user?.role === 'admin' ? '/activities/all' : '/activities/my-activities';
      const response = await api.get(endpoint);
      
      // Handle both array and object with items structure
      if (Array.isArray(response.data)) {
        setActivities(response.data);
      } else if (response.data?.items) {
        setActivities(response.data.items);
      } else {
        setActivities([]);
      }
    } catch (error) {
      toast.error('Erro ao carregar atividades');
      console.error(error);
      setActivities([]);
    }
  };

  const fetchDeals = async () => {
    try {
      const response = await api.get('/deals');
      setDeals(response.data?.items || []);
    } catch (error) {
      console.error('Error fetching deals:', error);
    }
  };

  const handleAddActivity = async () => {
    try {
      if (!newActivity.deal_id) {
        toast.error('Selecione um negócio para a atividade');
        return;
      }

      // Enviar a data diretamente sem converter para UTC
      // O input datetime-local retorna no formato "2025-12-11T17:50"
      // Adicionamos ":00" para segundos se não existir
      let dueDate = null;
      if (newActivity.due_date) {
        dueDate = newActivity.due_date.includes(':00') ? newActivity.due_date : newActivity.due_date + ':00';
      }

      await api.post(`/deals/${newActivity.deal_id}/activities`, {
        activity_type: newActivity.activity_type,
        subject: newActivity.subject,
        description: newActivity.description,
        due_date: dueDate,
        duration_minutes: newActivity.duration_minutes
      });

      toast.success('Atividade criada com sucesso!');
      setShowAddModal(false);
      resetNewActivity();
      await fetchAllActivities();
    } catch (error) {
      toast.error('Erro ao criar atividade');
    }
  };

  const resetNewActivity = () => {
    setNewActivity({
      activity_type: 'call',
      subject: '',
      description: '',
      due_date: '',
      duration_minutes: 30,
      deal_id: ''
    });
  };

  const handleCompleteActivity = async (activity) => {
    try {
      await api.put(`/activities/${activity.id}/complete`);
      toast.success('Atividade marcada como concluída! ✅');
      await fetchAllActivities();
    } catch (error) {
      toast.error('Erro ao marcar atividade como concluída');
    }
  };

  const getActivityIcon = (type) => {
    switch(type) {
      case 'call': return '📞';
      case 'email': return '📧';
      case 'meeting': return '🤝';
      case 'task': return '✅';
      case 'note': return '📝';
      default: return '📋';
    }
  };

  const getActivityTypeLabel = (type) => {
    switch(type) {
      case 'call': return 'Ligação';
      case 'email': return 'Email';
      case 'meeting': return 'Reunião';
      case 'task': return 'Tarefa';
      case 'note': return 'Anotação';
      default: return 'Atividade';
    }
  };

  const getActivityStatus = (activity) => {
    if (activity.completed) return 'completed';
    if (activity.due_date && new Date(activity.due_date) < new Date()) return 'overdue';
    return 'pending';
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'completed': return 'Concluída';
      case 'overdue': return 'Atrasada';
      case 'pending': return 'Pendente';
      default: return 'Indefinido';
    }
  };

  const filterActivities = () => {
    let filtered = activities;
    
    // Filter by view mode (date)
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);

    switch(viewMode) {
      case 'hoje':
        filtered = filtered.filter(activity => {
          if (!activity.due_date) return false;
          const activityDate = new Date(activity.due_date);
          return activityDate.toDateString() === today.toDateString();
        });
        break;
      case 'proximos':
        filtered = filtered.filter(activity => {
          if (!activity.due_date) return false;
          const activityDate = new Date(activity.due_date);
          return activityDate >= today && activityDate <= nextWeek;
        });
        break;
      case 'semana':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        
        filtered = filtered.filter(activity => {
          if (!activity.due_date) return false;
          const activityDate = new Date(activity.due_date);
          return activityDate >= startOfWeek && activityDate <= endOfWeek;
        });
        break;
      case 'todas':
      default:
        // No date filtering
        break;
    }
    
    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(activity => activity.activity_type === filterType);
    }
    
    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(activity => getActivityStatus(activity) === filterStatus);
    }
    
    return filtered;
  };

  const getActivityStats = () => {
    const today = new Date();
    const todayActivities = activities.filter(activity => {
      if (!activity.due_date) return false;
      return new Date(activity.due_date).toDateString() === today.toDateString();
    });
    
    const overdueActivities = activities.filter(activity => 
      activity.due_date && new Date(activity.due_date) < today && !activity.completed
    );
    
    const completedToday = todayActivities.filter(activity => activity.completed);
    
    return {
      total: activities.length,
      today: todayActivities.length,
      overdue: overdueActivities.length,
      completed_today: completedToday.length
    };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i} className="p-6">
              <div className="h-4 bg-slate-200 rounded w-1/4 mb-2"></div>
              <div className="h-4 bg-slate-200 rounded w-1/2"></div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const filteredActivities = filterActivities();
  const stats = getActivityStats();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="activities-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Atividades</h1>
          <p className="text-slate-600 mt-1">Gerencie suas atividades e compromissos</p>
        </div>
        
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button className="btn-primary">
              ➕ Nova Atividade
            </Button>
          </DialogTrigger>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass border-white/20">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
            <p className="text-sm text-slate-600">Total de Atividades</p>
          </CardContent>
        </Card>
        
        <Card className="glass border-white/20">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{stats.today}</div>
            <p className="text-sm text-slate-600">Hoje</p>
          </CardContent>
        </Card>
        
        <Card className="glass border-white/20">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{stats.overdue}</div>
            <p className="text-sm text-slate-600">Atrasadas</p>
          </CardContent>
        </Card>
        
        <Card className="glass border-white/20">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{stats.completed_today}</div>
            <p className="text-sm text-slate-600">Concluídas Hoje</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Controls */}
      <Card className="glass border-white/20">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* View Mode */}
            <div className="flex items-center space-x-1 bg-slate-100 p-1 rounded-lg">
              <Button
                variant={viewMode === 'hoje' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('hoje')}
              >
                📅 Hoje
              </Button>
              <Button
                variant={viewMode === 'proximos' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('proximos')}
              >
                📆 Próximos
              </Button>
              <Button
                variant={viewMode === 'semana' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('semana')}
              >
                🗓️ Semana
              </Button>
              <Button
                variant={viewMode === 'todas' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('todas')}
              >
                📋 Todas
              </Button>
            </div>

            {/* Type Filter */}
            <div className="flex items-center space-x-2">
              <Label>Tipo:</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-40 bg-white/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">🎯 Todos</SelectItem>
                  <SelectItem value="call">📞 Ligações</SelectItem>
                  <SelectItem value="email">📧 Emails</SelectItem>
                  <SelectItem value="meeting">🤝 Reuniões</SelectItem>
                  <SelectItem value="task">✅ Tarefas</SelectItem>
                  <SelectItem value="note">📝 Anotações</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div className="flex items-center space-x-2">
              <Label>Status:</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40 bg-white/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">🎯 Todos</SelectItem>
                  <SelectItem value="pending">🟡 Pendentes</SelectItem>
                  <SelectItem value="completed">🟢 Concluídas</SelectItem>
                  <SelectItem value="overdue">🔴 Atrasadas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activities List */}
      <div className="space-y-4" data-testid="activities-list">
        {filteredActivities.length === 0 ? (
          <Card className="glass border-white/20">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Nenhuma atividade encontrada</h3>
              <p className="text-slate-500">Crie sua primeira atividade ou ajuste os filtros</p>
            </CardContent>
          </Card>
        ) : (
          filteredActivities.map((activity) => (
            <Card key={activity.id} className="glass border-white/20 hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  {/* Activity Icon */}
                  <div className="text-3xl">
                    {getActivityIcon(activity.activity_type)}
                  </div>

                  {/* Activity Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-slate-900">{activity.subject}</h3>
                        <p className="text-sm text-slate-600">{getActivityTypeLabel(activity.activity_type)}</p>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Badge className={getStatusColor(getActivityStatus(activity))}>
                          {getStatusLabel(getActivityStatus(activity))}
                        </Badge>
                        
                        {!activity.completed && (
                          <Button
                            size="sm"
                            onClick={() => handleCompleteActivity(activity)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            ✓ Concluir
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Activity Details */}
                    <div className="space-y-2">
                      {activity.description && (
                        <p className="text-sm text-slate-600">{activity.description}</p>
                      )}

                      <div className="flex items-center space-x-4 text-xs text-slate-500">
                        {activity.due_date && (
                          <span>📅 {new Date(activity.due_date).toLocaleString('pt-BR')}</span>
                        )}
                        {activity.duration_minutes && (
                          <span>⏱️ {activity.duration_minutes} min</span>
                        )}
                        <span>📋 {activity.deal_title}</span>
                        <span>💰 {formatCurrency(activity.deal_value)}</span>
                        <span>📍 {activity.deal_stage}</span>
                        {user?.role === 'admin' && activity.owner_name && (
                          <span>👤 {activity.owner_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add Activity Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Nova Atividade</DialogTitle>
            <DialogDescription>Crie uma nova atividade vinculada a um negócio</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Atividade *</Label>
                <Select 
                  value={newActivity.activity_type}
                  onValueChange={(value) => setNewActivity({...newActivity, activity_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">📞 Ligação</SelectItem>
                    <SelectItem value="email">📧 Email</SelectItem>
                    <SelectItem value="meeting">🤝 Reunião</SelectItem>
                    <SelectItem value="task">✅ Tarefa</SelectItem>
                    <SelectItem value="note">📝 Anotação</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Duração (minutos)</Label>
                <Input
                  type="number"
                  value={newActivity.duration_minutes}
                  onChange={(e) => setNewActivity({...newActivity, duration_minutes: parseInt(e.target.value) || 30})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Negócio Vinculado *</Label>
              <Select 
                value={newActivity.deal_id}
                onValueChange={(value) => setNewActivity({...newActivity, deal_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um negócio" />
                </SelectTrigger>
                <SelectContent>
                  {deals.map((deal) => (
                    <SelectItem key={deal.id} value={deal.id}>
                      {deal.title} - {formatCurrency(deal.value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Assunto *</Label>
              <Input
                value={newActivity.subject}
                onChange={(e) => setNewActivity({...newActivity, subject: e.target.value})}
                placeholder="Ex: Follow-up sobre proposta"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Data e Hora</Label>
              <Input
                type="datetime-local"
                value={newActivity.due_date}
                onChange={(e) => setNewActivity({...newActivity, due_date: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Textarea
                value={newActivity.description}
                onChange={(e) => setNewActivity({...newActivity, description: e.target.value})}
                placeholder="Detalhes da atividade..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAddActivity} 
              className="btn-primary"
              disabled={!newActivity.subject || !newActivity.deal_id}
            >
              Criar Atividade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}